import { resolveDocumentCollectionElement } from "@needle-tools/addressing/document";
import { joinPath } from "@needle-tools/addressing";
import { viaForResource } from "../../../../platform/ai/common/target.js";
import { ServicesAccessor } from "../../../../platform/instantiation/common/instantiation";
import { IHierarchyService } from "../../../../platform/hierarchy/common/hierarchy";
import { ILogService } from "../../../../platform/log/common/log";
import { splitAddress } from "../../../../platform/properties/common/propertyPath";
import { resolveObjectHandle } from "../../../../platform/webgl/common/objectHandle";
import { IObjectTargetService, type OverTarget } from "../../project/common/documentTarget";
import { IProjectService } from "../../project/common/project";
import type { IOperationService } from "./operations";

/**
 * PRODUCER-side conversion for set-property ops (plans/operation-bridge.md 1f):
 * live property row → `{ target, path }`. This is where the row vocabulary
 * (vocabulary B — synthetic deep owners self-described by `metadata.address`)
 * is translated into the persistent address vocabulary at EMIT time; the fold
 * only ever sees addresses.
 */

export type PropertyOpTarget = { target: OverTarget; path: string };

function propertyTarget(target: OverTarget, path: string): PropertyOpTarget {
    // Component prefixes also identify the component type during folding.
    // Native graph prefixes are simply part of the property's document path.
    if (!target.pathPrefix || guidFromPathPrefix(target.pathPrefix)) return { target, path };
    return { target: { ref: target.ref, ...(target.address ? { address: target.address } : {}) }, path: target.pathPrefix + path };
}

/** what a producer knows about the edited property — structurally satisfied by
 * IProperty rows and by the minimal descriptors commands build */
export type PropertyLike = {
    readonly key: string;
    readonly owner: object;
    readonly metadata?: { readonly address?: unknown; readonly addressOwner?: object; readonly editScope?: "document" | "editor" };
};

export function propertyOpTargetFor(accessor: ServicesAccessor, property: PropertyLike): PropertyOpTarget | null {
    return propertyOpTargetVia({
        targets: accessor.getIfExists(IObjectTargetService) as IObjectTargetService | undefined,
        logger: accessor.getIfExists(ILogService) as ILogService | undefined,
        projects: accessor.getIfExists(IProjectService) as IProjectService | undefined,
        hierarchy: accessor.getIfExists(IHierarchyService) as IHierarchyService | undefined,
    }, property);
}

/** the same conversion for code that resolved services BEFORE an await.
 * `deps.operations` routes a miss through the H4 capture-miss channel
 * (journal + dev-throw) — pass it wherever it exists. */
export function propertyOpTargetVia(
    deps: {
        targets: Pick<IObjectTargetService, "targetFor" | "objectFor"> | undefined;
        logger: ILogService | undefined;
        projects: IProjectService | undefined;
        hierarchy: IHierarchyService | undefined;
        operations?: Pick<IOperationService, "reportCaptureMiss">;
    },
    property: PropertyLike,
): PropertyOpTarget | null {
    if (property.metadata?.editScope === "editor") return null;
    // Products without addressing still use the legacy changeset at the command.
    if (!deps.targets) return null;
    const resourceTarget = (object: object, path: string): PropertyOpTarget | null => {
        const via = deps.hierarchy && viaForResource(deps.targets, deps.hierarchy, object);
        if (!via?.holder.ref) return null;
        const segments: string[] = [via.slot];
        if (via.index !== undefined) segments.push(String(via.index));
        if (via.texture) segments.push(via.texture);
        return {
            target: { ref: via.holder.ref, ...(via.holder.address ? { address: via.holder.address } : {}) },
            path: `${joinPath(segments)}/${path}`,
        };
    };
    const owner = property.owner;
    if (owner && typeof owner === "object") {
        const direct = deps.targets.targetFor(owner);
        if (direct) return propertyTarget(direct, property.key);
    }
    // SYNTHETIC deep row (texture settings, nested rows): `metadata.address`
    // names the real anchor + path — resolve the anchor NOW (it is live at
    // emit; session handles are never persisted) and address THAT
    const deep = resolveDeepPropertyTarget({
        openFiles: deps.projects?.openFiles ?? [],
        roots: deps.hierarchy?.roots ?? [],
    }, property);
    if (deep) {
        const anchor = deps.targets.targetFor(deep.object);
        if (anchor) return propertyTarget(anchor, deep.path);
        const resource = resourceTarget(deep.object, deep.path);
        if (resource) return resource;
    }
    const resource = owner as { isMaterial?: boolean; isTexture?: boolean; isBufferGeometry?: boolean };
    if (deps.hierarchy && (resource?.isMaterial || resource?.isTexture || resource?.isBufferGeometry)) {
        const resolved = resourceTarget(owner, property.key);
        if (resolved) return resolved;
    }
    if (isEditorOnlyOwner(owner)) {
        // Editor-only visuals — grid and light helpers, gizmos, selection
        // outlines, a mesh-bake preview — have NO document identity by design:
        // they are never saved, never synced, and an edit to one is not an
        // operation. So this is not a stamping hole and must not be reported as
        // one. It matters beyond noise: in a dev web-editor build the miss
        // disposition is "throw", so toggling a helper's visibility would take
        // the property command down with it.
        return null;
    }

    const name = (owner as { name?: string } | undefined)?.name || property.key;
    deps.logger?.warn(`Operations: property '${name}.${property.key}' is not addressable — the edit is NOT captured as an operation (stamping hole?)`);
    deps.operations?.reportCaptureMiss({ kind: "set-property", reason: `property '${name}.${property.key}' is not addressable (stamping hole?)`, detail: { name, key: property.key } });
    return null;
}

/**
 * Written by `markEditorOnly` (platform/webgl/three.js/editorOnly.ts).
 *
 * Read through the REGISTERED symbol rather than importing that module: it
 * imports three, and this file must stay free of it. `Symbol.for` is the
 * cross-module contract — same key, any module, no dependency.
 */
const $editorOnly = Symbol.for("needle-editor:editor-only-object");

/** The mark sits on the subtree ROOT, so a child attached later inherits it. */
function isEditorOnlyOwner(owner: unknown): boolean {
    const seen = new Set<unknown>(); // a malformed scene can have a parent cycle
    for (let node: unknown = owner; node && typeof node === "object" && !seen.has(node);) {
        seen.add(node);
        if ((node as Record<symbol, unknown>)[$editorOnly] === true) return true;
        node = (node as { parent?: unknown }).parent;
    }
    return false;
}

/** the shape resolveObjectHandle walks — three objects and hierarchy owners both fit */
type GraphObjectLike = { id?: unknown; uuid?: unknown; children?: readonly unknown[] } | null | undefined;

type DeepTargetDeps = {
    readonly openFiles: ReadonlyArray<{ gltf?: { scene?: unknown; parser?: { associations?: Map<object, unknown> } } | null }>;
    readonly roots: ReadonlyArray<{ owner?: unknown }>;
};

/**
 * `metadata.address` → the live element + "/"-path. Two lead-segment forms:
 * - three.js `object.id` (numeric, session-scoped): resolved via getObjectById
 *   within open reference roots (three ids are session-unique, and resolution is
 *   scoped to the open files anyway — duplicated needle-ids are irrelevant here).
 * - element `uuid` (ORPHAN-inspected materials/textures — no scene-object context):
 *   resolved against the open files' parser ASSOCIATIONS (any file-backed element is
 *   keyed there). Materials become material-typed targets downstream; element kinds
 *   without an over-target form yet are skipped later with a reason.
 */
export function resolveDeepPropertyTarget(deps: DeepTargetDeps, property: { metadata?: { address?: unknown; addressOwner?: object } }): { object: object; path: string } | null {
    const address = property.metadata?.address;
    if (typeof address !== "string") return null;
    // splitAddress takes both grammars: rows mint document paths now, stored AI
    // arguments and unmigrated call sites may still arrive dotted
    const split = splitAddress(address);
    if (!split) return null;
    const { ownerId, path } = split;
    const owner = property.metadata?.addressOwner;
    if (owner && (String((owner as { id?: unknown }).id) === ownerId || (owner as { uuid?: unknown }).uuid === ownerId)) {
        return { object: owner, path };
    }
    // COMPONENT anchor ("component:<identity guid>"): rows owned by a nested
    // sub-object of a component (VolumeParameter — bloom threshold, #97) mint
    // these; the parameter object itself is unaddressable. Identity per
    // componentIdentityGuid: editor guid or runtime guid, matched by
    // componentByGuid on every node of the composition.
    if (ownerId.startsWith("component:")) {
        const guid = ownerId.substring("component:".length);
        const scopes: GraphObjectLike[] = [
            ...deps.openFiles.map(open => open.gltf?.scene as GraphObjectLike),
            ...deps.roots.map(root => root.owner as GraphObjectLike),
        ];
        for (const scope of scopes) {
            const found = findComponentInSubtree(scope, guid);
            if (found) return { object: found, path };
        }
        return null;
    }
    if (/^\d+$/.test(ownerId)) {
        for (const open of deps.openFiles) {
            const root = open.gltf?.scene as { getObjectById?(id: number): unknown } | undefined;
            const found = typeof root?.getObjectById === "function" ? root.getObjectById(Number(ownerId)) : null;
            if (found && typeof found === "object") return { object: found, path };
        }
        // FLAT MODEL: content children live LOOSE at the composition root — no
        // open file's subtree contains them, so a reloaded content node's deep
        // edits (material rows) resolved nothing and DROPPED silently (Marcel
        // 2026-08-04: the post-reload blue color never reached the document).
        // The hierarchy roots hold the edit scene — search the whole composition.
        for (const rootNode of deps.roots) {
            const owner = rootNode.owner as { getObjectById?(id: number): unknown } | undefined;
            const found = typeof owner?.getObjectById === "function" ? owner.getObjectById(Number(ownerId)) : null;
            if (found && typeof found === "object") return { object: found, path };
        }
        return null;
    }
    // uuid lead, SCENE OBJECT: rows mint the object's uuid now (the numeric id
    // is a per-instance counter). Associations below only key FILE-BACKED
    // elements, so a user-born node — loose at the composition root under the
    // flat model — is found the same way the numeric lead finds it: by walking.
    for (const open of deps.openFiles) {
        const found = resolveObjectHandle(open.gltf?.scene as GraphObjectLike, ownerId);
        if (found) return { object: found.object, path };
    }
    for (const rootNode of deps.roots) {
        const found = resolveObjectHandle(rootNode.owner as GraphObjectLike, ownerId);
        if (found) return { object: found.object, path };
    }
    // uuid lead, ELEMENT: materials and textures have no scene-graph home —
    // the parser associations key every file-backed one
    for (const open of deps.openFiles) {
        const associations = open.gltf?.parser?.associations;
        if (!associations) continue;
        for (const element of associations.keys()) {
            if ((element as { uuid?: string }).uuid === ownerId) return { object: element, path };
        }
    }
    return null;
}

/** the component with this identity guid anywhere under `root` (children walk) */
function findComponentInSubtree(root: GraphObjectLike, guid: string): object | null {
    if (!root || typeof root !== "object") return null;
    const stack: GraphObjectLike[] = [root];
    while (stack.length > 0) {
        const node = stack.pop();
        if (!node || typeof node !== "object") continue;
        const component = componentByGuid(node as object, guid);
        if (component) return component;
        const children = (node as { children?: readonly unknown[] }).children;
        if (Array.isArray(children)) {
            for (const child of children) stack.push(child as GraphObjectLike);
        }
    }
    return null;
}

/** the component guid a target's `pathPrefix` names ("components/guid:<id>/") —
 * the fold re-resolves the live component from host + guid with it */
export function guidFromPathPrefix(pathPrefix: string | undefined): string | null {
    const match = pathPrefix?.match(/^components\/guid:([^/]+)\//);
    return match ? match[1] : null;
}

/** a component on its host by editor guid — ops carry identity, consumers
 * re-resolve the live instance here (both the stamped editor guid and the
 * runtime `guid` field count; add stamps both) */
export function componentByGuid(host: object, guid: string): object | null {
    const components = (host as { userData?: { components?: unknown[] } }).userData?.components ?? [];
    return resolveDocumentCollectionElement(components, `guid:${guid}`) as object ?? null;
}
